import { IIndicator, IInsight } from '@jumpstart/core';
import LinkIcon from '@mui/icons-material/Link';
import Alert, { AlertColor } from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import { SnackbarCloseReason } from '@mui/material/Snackbar/Snackbar';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../core/firebase';
import InsightNotificationsService from './insight-notifications-service';
import { InsightService } from './insight-service';
import { uiExternalUrls } from './states';
import EventEditor from './widgets/EventEditor';
import IndicatorsList from './widgets/IndicatorsList';
import { IValidationData, validateDefault, validateInsight } from './widgets/utils';
import { ValidationPrompters } from './widgets/ValidationPrompter';


export default function EventPage() {
  const [user ] = useAuthState(auth);
  const params = useParams<{ event: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<IInsight | null>(null);
  const [validation, setValidation] = useState<IValidationData>(validateDefault());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: AlertColor } | null>(null);
  const [indicator, setIndicator] = useState<IIndicator | null>(null);

  const reloadEvent = () => {
    setLoading(true);
    setEvent(null);
    if (!params.event) {
      return;
    }
    return InsightService.get(params.event)
      .then((e: IInsight | null) => {
        setMessage({ text: `Successfully reloaded event "${e?.name}"`, severity: 'success' });
        setLoading(false);
        setEvent(e);
      })
      .catch((err: Error) => {
        console.error(err);
        setMessage({ text: `Failed to get event "${params.event}". Error ${err.message}`, severity: 'error' });
        setLoading(false);
        setEvent(null);
      });
  };

  const handleError = (err: Error) => {
    setMessage({ text: err.message, severity: 'error' });
    console.error(err);
    setLoading(false);
  };

  const handleCloseSnack = (event: React.SyntheticEvent<any> | Event, reason: SnackbarCloseReason) => {
    if (reason && reason === 'clickaway') {
      return;
    }
    setMessage(null);
  };

  const handleEditEvent = (newEvent: IInsight): void => {
    const { content, ...remaining } = newEvent;
    console.log('UPDATING', remaining);
    if (loading) {
      return;
    }
    setLoading(true);
    InsightService.updateRoot(remaining)
      .then(reloadEvent)
      .then(() => {
        return InsightNotificationsService.insight(newEvent, user as any, 'updated');
      })
      .catch(handleError);
  };

  const handleUpdateIndicators = (indicators: IIndicator[]): void => {
    if (!event) {
      console.error('No insight provided');
      return;
    }
    const newInsight = { ...event };
    newInsight.content.indicators = indicators.map((i: IIndicator, index) => {
      i.id = `${index}`;
      return i;
    });
    setEvent(newInsight);
    setLoading(true);
    InsightService.updateContent({ id: newInsight.id, content: newInsight.content })
      .then(reloadEvent)
      .then(() => InsightNotificationsService.insight(newInsight, user as any, 'updated'))
      .catch(handleError);
  };

  const handleDeleteIndicator = (indicator: IIndicator & { index?: number }) => {
    if (!event) {
      return;
    }
    const newEvent = { ...event };
    const index: number = newEvent.content.indicators.findIndex(i => i.id === indicator.id);
    if (index > -1) {
      newEvent.content.indicators.splice(index, 1);
      handleUpdateIndicators(newEvent.content.indicators);
    }
  };

  const handleAddIndicator = (indicator: IIndicator & { index?: number }) => {
    if (!event) {
      return;
    }
    const newEvent = { ...event };
    let to: number | undefined = indicator.index;
    const from: number = newEvent.content.indicators.findIndex(i => i.id === indicator.id);
    if (from === -1) {
      // No from, must not be part of the array, add it.
      newEvent.content.indicators.push(indicator);
    } else {
      // Swap previous
      newEvent.content.indicators.splice(from, 1, indicator);
    }

    if (typeof to === 'number') {
      // reorder
      newEvent.content.indicators.splice(to, 0, newEvent.content.indicators.splice(from, 1)[0]);
    }

    handleUpdateIndicators(newEvent.content.indicators);
  };

  const handleSelectIndicator = (indicator: IIndicator & { index?: number }) => {
    if (!event) {
      return null;
    }
    const index: number = event.content.indicators.findIndex(i => i.id === indicator.id);
    if (index === -1) {
      setMessage({ text: `Failed to get indicator "${indicator.id}".`, severity: 'error' });
      console.error(`Failed`, indicator.id);
      return;
    } else {
      navigate(`/admin/events/indicator/${event.id}/${event.content.indicators[index].id}`);
    }
  };

  useEffect(() => {
    if (params.event) {
      reloadEvent();
    } else {
      setEvent(null);
    }
  }, [params.event]);

  useEffect(() => {
    if (event) {
      setValidation(validateInsight(event));
    } else {
      setValidation(validateDefault());
    }
  }, [event]);

  if (loading) {
    return null;
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <Grid container direction="column">
        <Snackbar open={!!message} autoHideDuration={6000} onClose={handleCloseSnack}>
          <Alert onClose={() => setMessage(null)} severity={message ? message.severity : 'warning'}>
            {message ? message.text : ''}
          </Alert>
        </Snackbar>
        <Grid item style={{ marginBottom: 24 }} container direction="row">
          <Breadcrumbs>
            <Link color="inherit" to={`/admin/events/select`} component={RouterLink}>
              Events
            </Link>
            {event && (
              <Grid container direction="row" wrap="nowrap">
                <Typography color="textPrimary">{event.name}</Typography>
                <Link
                  href={uiExternalUrls.indicators(event)}
                  target={`scout9-beta-mobile-${event.id}`}
                  style={{ height: 20, marginLeft: 8 }}
                >
                  <LinkIcon fontSize="small"/>
                </Link>
              </Grid>
            )}
          </Breadcrumbs>
        </Grid>

        <Grid container direction="row" style={{padding: 24}}>
          <Grid item xs={6}>
            <Paper style={{ padding: 24, height: '100%' }}>
              <Grid container direction="column">

                <Typography variant="h6" style={{ marginBottom: 8 }}>Indicators</Typography>
                <Typography variant="caption" style={{ marginBottom: 32 }}>Select or modify the indicators
                  below</Typography>
                {event && <IndicatorsList
                  validation={{...validation.errors, ...validation.warnings}}
                  event={event}
                  onDelete={handleDeleteIndicator}
                  onAdd={handleAddIndicator}
                  onSelect={handleSelectIndicator}
                  selected={indicator}
                  maxId={event.content.indicators.length - 1}
                />}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={1}/>

          {(validation.numErrors > 0 || validation.numWarnings > 0) &&
          <Grid item xs={5}>
            <Paper style={{ padding: 24, height: '100%' }}>
              <Grid container direction="column">
                <ValidationPrompters validation={validation}/>
              </Grid>
            </Paper>
          </Grid>}

          {/*{event && <EventFormEdit event={event} onSubmit={handleEditEvent}/>}*/}
        </Grid>

        {event && <EventEditor event={event} onSubmit={handleEditEvent} loading={loading} validation={{...validation.errors, ...validation.warnings}}/>}
      </Grid>
    </Container>
  );
}
